import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatPhoneForDisplay } from '../utils/phone';

function AdminPanel({ token, onStartSupportSession }) {
  const calendarBlue = '#539c98';
  const actionButtonStyle = {
    background: calendarBlue,
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const compactTenantInputStyle = {
    width: '120px'
  };

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedAccessRequestId, setSelectedAccessRequestId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [supportTenantId, setSupportTenantId] = useState('');
  const [supportRole, setSupportRole] = useState('office');
  const [roleDraftByUserId, setRoleDraftByUserId] = useState({});
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantEmail, setNewTenantEmail] = useState('');
  const [newTenantLogoName, setNewTenantLogoName] = useState('logo.svg');
  const [newTenantStripeAccountId, setNewTenantStripeAccountId] = useState('');
  const [error, setError] = useState('');

  function normalizeSlugFromName(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
  }

  function truncate(value = '', max = 12) {
    const text = String(value || '').trim();
    if (!text) return '-';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }

  function getStripeStatusLabel(tenant) {
    const status = String(tenant?.stripe_onboarding_status || '').trim().toLowerCase();
    if (status === 'connected') return 'Connected';
    if (status === 'incomplete') return 'Incomplete';
    if (status === 'error') return 'Status Error';
    return 'Not Started';
  }

  function getTenantLabel(tenantId) {
    if (!tenantId) return '-';
    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) return tenantId;
    return `${tenant.name} (${tenant.slug})`;
  }

  const loadUsers = useCallback(async function loadUsers() {
    try {
      const res = await axios.get(`/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedUsers = Array.isArray(res.data) ? res.data : [];
      setUsers(fetchedUsers);
      setRoleDraftByUserId(prev => {
        const next = { ...prev };
        fetchedUsers.forEach(user => {
          if (!next[user.id]) {
            next[user.id] = user.role || 'user';
          }
        });
        Object.keys(next).forEach((userId) => {
          if (!fetchedUsers.some(user => user.id === userId)) {
            delete next[userId];
          }
        });
        return next;
      });
      if (!selectedUserId && fetchedUsers.length > 0) {
        setSelectedUserId(fetchedUsers[0].id);
      }
    } catch (err) {
      console.error('Failed to load users', err);
      setError('Failed to load users');
    }
  }, [token, selectedUserId]);

  const loadTenants = useCallback(async function loadTenants() {
    try {
      const res = await axios.get(`/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(res.data || []);
      if (!selectedTenantId && Array.isArray(res.data) && res.data.length > 0) {
        setSelectedTenantId(res.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load tenants', err);
      setError('Failed to load tenants');
    }
  }, [token, selectedTenantId]);

  const loadAccessRequests = useCallback(async function loadAccessRequests() {
    try {
      const res = await axios.get(`/access-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load access requests', err);
      setError('Failed to load access requests');
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
    loadTenants();
    loadAccessRequests();
  }, [loadUsers, loadTenants, loadAccessRequests]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadAccessRequests();
    }, 15000);
    return () => clearInterval(timer);
  }, [loadAccessRequests]);

  useEffect(() => {
    if (!Array.isArray(tenants) || tenants.length === 0) return;
    if (supportTenantId && tenants.some(tenant => tenant.id === supportTenantId)) return;

    const dummyTenant = tenants.find(tenant => String(tenant.slug || '').trim().toLowerCase() === 'default');
    setSupportTenantId(dummyTenant?.id || tenants[0].id);
  }, [tenants, supportTenantId]);

  async function handleCreateTenant(e) {
    e.preventDefault();
    const name = newTenantName.trim();
    const slug = normalizeSlugFromName(newTenantName);
    if (!name) {
      alert('Tenant name is required');
      return;
    }

    try {
      await axios.post(
        `/tenants`,
        {
          name,
          slug,
          email: newTenantEmail.trim() || null,
          logo_name: newTenantLogoName.trim() || null,
          stripe_account_id: newTenantStripeAccountId.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTenantName('');
      setNewTenantEmail('');
      setNewTenantLogoName('logo.svg');
      setNewTenantStripeAccountId('');
      await loadTenants();
      alert('Tenant created');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create tenant');
    }
  }

  const selectedTenant = tenants.find(t => t.id === selectedTenantId) || null;
  const selectedAccessRequest = accessRequests.find(request => request.id === selectedAccessRequestId) || null;
  const selectedUser = users.find(user => user.id === selectedUserId) || null;
  const selectedTenantStripeStatus = getStripeStatusLabel(selectedTenant);
  const showSelectedTenantStripeStatus = Boolean(
    selectedTenant && String(selectedTenant?.stripe_onboarding_status || '').trim().toLowerCase() !== 'connected'
  );

  useEffect(() => {
    if (!selectedAccessRequestId) return;
    if (!accessRequests.some(request => request.id === selectedAccessRequestId)) {
      setSelectedAccessRequestId('');
    }
  }, [accessRequests, selectedAccessRequestId]);

  async function handleEditSelectedTenant() {
    if (!selectedTenant) return;

    const name = window.prompt('Tenant name:', selectedTenant.name || '');
    if (!name || !name.trim()) return;

    const email = window.prompt('Tenant email:', selectedTenant.email || '');
    if (email === null) return;

    const logoName = window.prompt('Logo name:', selectedTenant.logo_name || 'logo.svg');
    if (logoName === null) return;

    const stripeAccountId = window.prompt('Stripe account id (acct_...):', selectedTenant.stripe_account_id || '');
    if (stripeAccountId === null) return;

    try {
      await axios.put(
        `/tenants/${selectedTenant.id}`,
        {
          name: name.trim(),
          slug: normalizeSlugFromName(name),
          email: email.trim() || null,
          logo_name: logoName.trim() || null,
          stripe_account_id: stripeAccountId.trim() || null,
          is_active: Boolean(selectedTenant.is_active)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadTenants();
      alert('Tenant updated');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tenant');
    }
  }

  async function handleToggleTenantActive(tenant) {
    if (!tenant) return;

    const isActive = Boolean(tenant.is_active);
    const confirmed = window.confirm(
      isActive
        ? 'Deactivate this tenant? Existing data remains, but tenant is marked inactive.'
        : 'Activate this tenant? Users for this tenant will be able to access the system again.'
    );
    if (!confirmed) return;

    try {
      if (isActive) {
        await axios.delete(`/tenants/${tenant.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(
          `/tenants/${tenant.id}`,
          {
            name: String(tenant.name || '').trim(),
            slug: String(tenant.slug || '').trim(),
            address: String(tenant.address || '').trim() || null,
            phone: String(tenant.phone || '').trim() || null,
            email: String(tenant.email || '').trim() || null,
            logo_name: String(tenant.logo_name || '').trim() || null,
            stripe_account_id: String(tenant.stripe_account_id || '').trim() || null,
            is_active: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await loadTenants();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${isActive ? 'deactivate' : 'activate'} tenant`);
    }
  }

  async function handleSendTenantOnboarding(tenant) {
    const defaultEmail = String(tenant?.email || '').trim();
    const email = window.prompt('Send onboarding email to:', defaultEmail);
    if (!email) return;

    try {
      await axios.post(
        `/tenants/${tenant.id}/send-onboarding`,
        { email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Onboarding email sent');
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send onboarding email');
    }
  }

  async function handleSaveSelectedUserRole() {
    if (!selectedUser) return;
    const nextRole = String(roleDraftByUserId[selectedUser.id] || selectedUser.role || 'user').trim().toLowerCase();
    if (!['user', 'office', 'admin'].includes(nextRole)) {
      alert('Role must be user, office, or admin');
      return;
    }

    try {
      await axios.put(`/users/${selectedUser.id}/role`, 
        { role: nextRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadUsers();
      alert('Role updated');
    } catch (err) {
      alert('Failed to update role');
    }
  }

  async function handleDeleteUser(id) {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadUsers();
    } catch (err) {
      alert('Failed to delete user');
    }
  }

  async function handleApproveAccessRequest(requestRow, createPlaceholderTenant = false) {
    let selectedTenant = null;
    if (!createPlaceholderTenant) {
      if (!Array.isArray(tenants) || tenants.length === 0) {
        alert('Create at least one tenant before approving requests, or use Approve + Create Tenant.');
        return;
      }

      const requestedTenantCode = String(requestRow?.requested_tenant_code || '').trim().toLowerCase();
      const suggestedTenantIndex = requestedTenantCode
        ? tenants.findIndex(t => String(t?.tenant_code || '').trim().toLowerCase() === requestedTenantCode)
        : 0;

      const tenantPrompt = tenants
        .map((tenant, index) => `${index + 1}) ${tenant.name} (${tenant.tenant_code || tenant.slug})`)
        .join('\n');
      const defaultChoice = String((suggestedTenantIndex >= 0 ? suggestedTenantIndex : 0) + 1);
      const tenantChoice = window.prompt(`Assign tenant by number:\n${tenantPrompt}`, defaultChoice);
      if (!tenantChoice) return;
      const tenantIndex = Number.parseInt(tenantChoice, 10) - 1;
      selectedTenant = tenants[tenantIndex];
      if (!selectedTenant) {
        alert('Invalid tenant selection');
        return;
      }
    }

    const suggestedRole = requestRow.requested_role === 'office' ? 'office' : 'user';
    const roleInput = String(window.prompt('Approved role (user or office):', suggestedRole) || '').trim().toLowerCase();
    if (!['user', 'office'].includes(roleInput)) {
      alert('Role must be user or office');
      return;
    }

    const sendOnboarding = window.confirm('Send tenant maintenance onboarding email now? (Use OK for yes, Cancel for no)');

    let tenantProfile = undefined;
    if (createPlaceholderTenant) {
      const suggestedName = String(requestRow?.business_name || requestRow?.full_name || '').trim() || 'New Tenant';
      const nameInput = window.prompt('Placeholder tenant name:', suggestedName);
      if (!nameInput || !nameInput.trim()) {
        alert('Tenant name is required');
        return;
      }

      tenantProfile = {
        name: nameInput.trim(),
        tenantCode: String(requestRow?.requested_tenant_code || '').trim(),
        email: String(requestRow?.business_email || requestRow?.email || '').trim(),
        phone: String(requestRow?.business_phone || requestRow?.requested_phone || '').trim(),
        address: String(requestRow?.billing_address || '').trim()
      };
    }

    const noteInput = window.prompt('Optional approval note:', '') || '';

    try {
      await axios.post(
        `/access-requests/${requestRow.id}/approve`,
        {
          tenantId: selectedTenant?.id,
          createTenant: createPlaceholderTenant,
          tenantProfile,
          role: roleInput,
          sendOnboarding,
          note: noteInput.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Access request approved');
      await loadAccessRequests();
      await loadUsers();
      await loadTenants();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve access request');
    }
  }

  async function handleDisapproveAccessRequest(requestRow) {
    const noteInput = window.prompt('Reason for disapproval (optional):', '') || '';

    try {
      await axios.post(
        `/access-requests/${requestRow.id}/disapprove`,
        { note: noteInput.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Access request disapproved');
      await loadAccessRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to disapprove access request');
    }
  }

  async function handleStartSupportSession() {
    const tenantId = String(supportTenantId || '').trim();
    const role = String(supportRole || 'office').trim().toLowerCase();
    if (!tenantId) {
      alert('Select a tenant first');
      return;
    }
    if (!['user', 'office', 'admin'].includes(role)) {
      alert('Role must be user, office, or admin');
      return;
    }

    try {
      const response = await axios.post(
        `/admin/support-session`,
        { tenantId, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const supportToken = String(response?.data?.token || '').trim();
      if (!supportToken) {
        alert('Support session token was not returned');
        return;
      }

      if (typeof onStartSupportSession === 'function') {
        onStartSupportSession(supportToken);
      } else {
        localStorage.setItem('calphender_token', supportToken);
        window.location.href = '/';
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start support session');
    }
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Admin Panel</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
        <h3>Support Session</h3>
        <div style={{ fontSize: '0.9rem', color: '#444', marginBottom: 10 }}>
          Assume a tenant role for Calendar/Reports troubleshooting. Defaults to dummy tenant (`default`).
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <select value={supportTenantId} onChange={(e) => setSupportTenantId(e.target.value)}>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.slug})
              </option>
            ))}
          </select>
          <select value={supportRole} onChange={(e) => setSupportRole(String(e.target.value || 'office').trim().toLowerCase())}>
            <option value="user">User</option>
            <option value="office">Office</option>
            <option value="admin">Admin</option>
          </select>
          <button type="button" style={actionButtonStyle} disabled={!supportTenantId} onClick={handleStartSupportSession}>
            Assume Tenant Role
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
        <h3>Tenant Management</h3>
        <form onSubmit={handleCreateTenant} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', flexWrap: 'nowrap', justifyContent: 'center', overflowX: 'auto' }}>
          <input 
            type="text"
            placeholder="Tenant name"
            value={newTenantName}
            onChange={e => setNewTenantName(e.target.value)}
            style={compactTenantInputStyle}
            required
          />
          <input 
            type="email"
            placeholder="Email"
            value={newTenantEmail}
            onChange={e => setNewTenantEmail(e.target.value)}
            style={compactTenantInputStyle}
          />
          <input
            type="text"
            placeholder="Logo file name (e.g. logo.svg)"
            value={newTenantLogoName}
            onChange={e => setNewTenantLogoName(e.target.value)}
            style={compactTenantInputStyle}
          />
          <input
            type="text"
            placeholder="Stripe account ID (acct_...)"
            value={newTenantStripeAccountId}
            onChange={e => setNewTenantStripeAccountId(e.target.value)}
            style={compactTenantInputStyle}
          />
        </form>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <button type="button" onClick={handleCreateTenant} style={actionButtonStyle}>Create Tenant</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', lineHeight: 1.4 }}>
          <thead>
            <tr style={{ textAlign: 'center', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '10px', width: '45px' }}>Sel</th>
              <th style={{ padding: '10px' }}>Name</th>
              <th style={{ padding: '10px' }}>Tenant Code</th>
              <th style={{ padding: '10px' }}>Email</th>
              <th style={{ padding: '10px' }}>Logo</th>
              <th style={{ padding: '10px' }}>Connect Account</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(tenant => (
              <tr key={tenant.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px' }}>
                  <input
                    type="radio"
                    name="tenant-select"
                    checked={selectedTenantId === tenant.id}
                    style={{ accentColor: '#539c98' }}
                    onChange={() => setSelectedTenantId(tenant.id)}
                  />
                </td>
                <td style={{ padding: '10px' }}>{truncate(tenant.name, 18)}</td>
                <td style={{ padding: '10px', fontWeight: 600 }}>{truncate(tenant.tenant_code, 14)}</td>
                <td style={{ padding: '10px' }}>{truncate(tenant.email, 18)}</td>
                <td style={{ padding: '10px' }}>{truncate(tenant.logo_name || 'logo.svg', 14)}</td>
                <td style={{ padding: '10px' }}>{truncate(tenant.stripe_account_id, 14)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button type="button" style={actionButtonStyle} onClick={handleEditSelectedTenant} disabled={!selectedTenant}>Edit</button>
          <button type="button" style={actionButtonStyle} onClick={() => selectedTenant && handleSendTenantOnboarding(selectedTenant)} disabled={!selectedTenant}>Send Onboarding</button>
          <button
            type="button"
            onClick={() => selectedTenant && handleToggleTenantActive(selectedTenant)}
            disabled={!selectedTenant}
            style={{
              background: selectedTenant?.is_active ? '#dc3545' : '#198754',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: selectedTenant ? 'pointer' : 'not-allowed',
              opacity: selectedTenant ? 1 : 0.6
            }}
          >
            {selectedTenant?.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
        {showSelectedTenantStripeStatus && (
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
            <input
              type="text"
              value={`Stripe Status: ${selectedTenantStripeStatus}`}
              readOnly
              style={{ width: '260px', textAlign: 'center', fontSize: '0.86rem' }}
            />
          </div>
        )}

      </div>

      <div style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0 }}>Pending Access Requests</h3>
          <button
            type="button"
            style={actionButtonStyle}
            onClick={loadAccessRequests}
          >
            Refresh
          </button>
        </div>
        {accessRequests.length === 0 ? (
          <div style={{ color: '#666' }}>No pending access requests.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '0.86rem', lineHeight: 1.4 }}>
            <thead>
              <tr style={{ textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '10px', width: '45px' }}>Sel</th>
                <th style={{ padding: '10px' }}>Name</th>
                <th style={{ padding: '10px' }}>Email</th>
                <th style={{ padding: '10px' }}>Tenant Code</th>
                <th style={{ padding: '10px' }}>Phone</th>
                <th style={{ padding: '10px' }}>Business Profile</th>
                <th style={{ padding: '10px' }}>Verified At</th>
              </tr>
            </thead>
            <tbody>
              {accessRequests.map(requestRow => (
                <tr key={requestRow.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px' }}>
                    <input
                      type="radio"
                      name="request-select"
                      checked={selectedAccessRequestId === requestRow.id}
                      style={{ accentColor: '#539c98' }}
                      onChange={() => setSelectedAccessRequestId(requestRow.id)}
                    />
                  </td>
                  <td style={{ padding: '10px' }}>{requestRow.full_name || '-'}</td>
                  <td style={{ padding: '10px' }}>{requestRow.email}</td>
                  <td style={{ padding: '10px', fontWeight: 600 }}>{requestRow.requested_tenant_code || '-'}</td>
                  <td style={{ padding: '10px' }}>{requestRow.requested_phone ? formatPhoneForDisplay(requestRow.requested_phone) : '-'}</td>
                  <td style={{ padding: '10px', fontSize: '0.86rem', lineHeight: 1.4, maxWidth: 220 }}>
                    <div><strong>{requestRow.business_name || '-'}</strong></div>
                    <div>{requestRow.business_email || '-'}</div>
                    <div>{requestRow.business_phone ? formatPhoneForDisplay(requestRow.business_phone) : '-'}</div>
                    <div>{requestRow.billing_address || '-'}</div>
                    <div>{requestRow.shipping_address ? `Ship: ${requestRow.shipping_address}` : ''}</div>
                  </td>
                  <td style={{ padding: '10px' }}>{new Date(requestRow.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            style={actionButtonStyle}
            onClick={() => selectedAccessRequest && handleApproveAccessRequest(selectedAccessRequest, false)}
            disabled={!selectedAccessRequest}
          >
            Approve
          </button>
          <button
            type="button"
            style={{ ...actionButtonStyle, background: '#588892' }}
            onClick={() => selectedAccessRequest && handleApproveAccessRequest(selectedAccessRequest, true)}
            disabled={!selectedAccessRequest}
          >
            Approve + Create Tenant
          </button>
          <button
            type="button"
            onClick={() => selectedAccessRequest && handleDisapproveAccessRequest(selectedAccessRequest)}
            disabled={!selectedAccessRequest}
            style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: selectedAccessRequest ? 'pointer' : 'not-allowed', opacity: selectedAccessRequest ? 1 : 0.6 }}
          >
            Disapprove
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
        <h3 style={{ marginTop: '18px' }}>User List</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', lineHeight: 1.4 }}>
          <thead>
            <tr style={{ textAlign: 'center', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '10px', width: '45px' }}>Sel</th>
              <th style={{ padding: '10px' }}>Email</th>
              <th style={{ padding: '10px' }}>Role</th>
              <th style={{ padding: '10px' }}>Tenant</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px' }}>
                  <input
                    type="radio"
                    name="user-select"
                    checked={selectedUserId === user.id}
                    style={{ accentColor: '#539c98' }}
                    onChange={() => setSelectedUserId(user.id)}
                  />
                </td>
                <td style={{ padding: '10px' }}>{user.email}</td>
                <td style={{ padding: '10px' }}>
                  <select 
                    value={roleDraftByUserId[user.id] || user.role || 'user'} 
                    onChange={(e) => setRoleDraftByUserId(prev => ({ ...prev, [user.id]: e.target.value }))}
                  >
                    <option value="user">User</option>
                    <option value="office">Office</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td style={{ padding: '10px' }}>{getTenantLabel(user.tenant_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            style={actionButtonStyle}
            onClick={handleSaveSelectedUserRole}
            disabled={!selectedUser}
          >
            Save Role
          </button>
          <button
            type="button"
            onClick={() => selectedUser && handleDeleteUser(selectedUser.id)}
            disabled={!selectedUser}
            style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: selectedUser ? 'pointer' : 'not-allowed', opacity: selectedUser ? 1 : 0.6 }}
          >
            Delete User
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
